/**
 * Javascript representation of the API resources
 */

const UserPolicyResource = {
  admin: Boolean,
  createGroup: Boolean,
};

const UserResource = {
  id: Number,
  username: String,
  email: String,
  firstName: String,
  lastName: String,
  bio: String,
  academicDegree: String,
  institutions: Array,
  followersCount: Number,
  followingCount: Number,
  followers: Array,
  following: Array,
  karma: Number,
  avatarUrl: String,
  socialNetworks: Array,
  contactMe: Boolean,
  institutionEmail: String,
  speciality: Object,
  profession: Object,
  licenseFile: String,
  lastActivities: Array,
  caseCount: Number,
  can: UserPolicyResource,
  becomeRadiologist: Number,
};

const InstitutionResource = {
  id: Number,
  name: String,
  integrationType: String,
  proxyId: String,
  proxyAet: String,
  isPrimary: Boolean,
};

const SectionResource = {
  id: Number,
  label: String,
};

const CasePolicyResource = {
  updateCase: Boolean,
  deleteCase: Boolean,
  verifyPatient: Boolean,
  requestAnalysis: Boolean,
  createReply: Boolean,
};

const CaseResource = {
  id: Number,
  caseNumber: String,
  title: String,
  details: String,
  diagnosis: DiagnosisResource,
  followUp: String,
  educationalValue: Number,
  educationalVotesCount: Number,
  status: String,
  isMultiStudy: Boolean,
  isFeatured: Boolean,
  tags: Array,
  repliesCount: Number,
  isAnonymous: Boolean,
  createdBy: UserResource,
  createdAt: String,
  updatedAt: String,
  publishedAt: String,
  subSpecialtySection: SectionResource,
  followersCount: Number,
  sharesCount: Number,
  keyImages: Array,
  caseImages: Array,
  removedImages: Array,
  newImages: Array,
  studies: Array,
  can: CasePolicyResource,
  links: Object,
  lastActivity: Object,
  isNew: Boolean,
  hasNewReply: Boolean,
  notificationsCount: Number,
  trashed: Boolean,
};

const MediaResource = {
  accessLink: String,
  authorId: Object,
  filepath: String,
  id: String,
  lastActivity: Object,
  thumbnailPicture: String,
  title: String,
  updatedAt: String,
};

const CaseCollectionResource = {
  id: Number,
  title: String,
  slug: String,
  description: String,
  casesCount: Number,
  followersCount: Number,
  createdAt: String,
  updatedAt: String,
  lastActivityAt: String,
  isFollowing: Boolean,
  author: UserResource,
  cases: Array,
  followers: Array,
  members: Array,
};

const ReplyPolicyResource = {
  updateReply: Boolean,
};

const ReplyResource = {
  id: Number,
  originCase: CaseResource,
  comment: String,
  diagnosis: DiagnosisResource,
  followUp: String,
  images: Array,
  isAiReply: Boolean,
  createdBy: UserResource,
  createdAt: String,
  updatedAt: String,
  can: ReplyPolicyResource,
};

const StudyResource = {
  id: Number,
  studyId: String,
  studyInstanceUid: String,
  studyDate: String,
  imported: Boolean,
  description: String,
  bodyPartExamined: String,
  modalities: Array,
  isDicomStudy: Boolean,
  keyImages: Array,
  seriesImages: Array,
  trashed: Boolean,
};

const DiagnosisResource = {
  details: String,
  status: String,
  confidenceLevel: Number,
  agreeVotesCount: Number,
  originCase: CaseResource,
  originReply: ReplyResource,
  createdBy: UserResource,
  agree: Boolean,
  voters: Array,
};

const TagResource = {
  id: Number,
  value: String,
  origin: String,
  isReplyTag: Boolean,
  reply: ReplyResource,
  count: Number,
};

const ThumbnailResource = {
  id: Number,
  reference: String,
  position: Number,
  imageUrl: String,
  imageType: String,
  originalWidth: Number,
  originalHeight: Number,
  hasAnnotations: Boolean,
  annotations: Array,
  isSupported: Boolean,
  originCase: CaseResource,
  originStudy: StudyResource,
  originReply: ReplyResource,
  instanceId: String,
  sopInstanceUid: String,
  addedBy: UserResource,
  createdAt: String,
  updatedAt: String,
};

const AnnotationResource = {
  id: Number,
  graphicType: String,
  originRow: Number,
  originColumn: Number,
  coordinates: Array,
  overlayMaskUrl: String,
  label: String,
};

const QuizStudyResource = {
  id: Number,
  title: String,
  slug: String,
  description: String,
  completed_pct: Number,
  starts_at: String,
  created_at: String,
  ends_at: String,
  state: String,
  cases: Array,
  users: Array,
  author: Object,
};

const QuizCaseResource = {
  id: Number,
  title: String,
  position: Number,
  location: String,
  studies: Array,
};

const RatingResource = {
  id: Number,
  educationalValue: Number,
  ratedCase: CaseResource,
  ratedBy: UserResource,
  createdAt: String,
  updatedAt: String,
};

const QuizQuestionResource = {
  id: Number,
  title: String,
  uuid: String,
  reference: String,
  position: Number,
  type: String,
  settings: Object,
  settingsValid: Boolean,
  settingsErrors: Object,
  createdAt: String,
};

const ApplicationEventLogResource = {
  id: Number,
  eventName: String,
  actor: UserResource,
  authenticatedId: Number,
  authenticatedName: String,
  institutionName: String,
  date: String,
  moreData: Object,
};

const ApplicationEventResource = {
  id: Number,
  name: String,
};

const AiProviderResource = {
  id: Number,
  name: String,
  engines: Array,
};

const AiEngineResource = {
  id: Number,
  name: String,
  brand: String,
  description: String,
  provider: AiProviderResource,
  user: UserResource,
  isFdaCertified: Boolean,
  isCeCertified: Boolean,
  ceIntendedUse: String,
};

const AnalysisResource = {
  broker: String,
  provider: String,
  engine: String,
  status: String,
  relatedCase: CaseResource,
  studies: Array,
  message: String,
  resources: Object,
  data: Object,
  attachments: Object,
  createdAt: String,
  updatedAt: String,
};

const CaseGroupResource = {
  id: Number,
  name: String,
  slug: String,
  description: String,
  avatarUrl: String,
  members_count: Number,
  cases_count: Number,
  members: Array,
  owners: Array,
  creator: UserResource,
  createdAt: String,
  updatedAt: String,
};

const PackageResource = {
  id: Number,
  type: Number,
  name: String,
  groups_limit: Number,
  group_members_limit: Number,
  group_cases_limit: Number,
  collection: Number,
};

const ResearchRepositoryResource = {
  id: Number,
  uuid: String,
  reference: String,
  name: String,
  description: String,
  createdBy: Object,
  updatedBy: Object,
  createdAt: String,
  updatedAt: String,
};

const ResearchProjectResource = {
  id: Number,
  name: String,
  description: String,
  uuid: String,
  reference: String,
  externalReference: String,
  status: String,
  firstPatientFirstScanDate: String,
  lastPatientLastScanDate: String,
  oqTurnaround: Number,
  createdBy: UserResource,
  updatedBy: UserResource,
  quickAccess: String,
  avatarUrl: String,
};

export {
  UserResource,
  InstitutionResource,
  SectionResource,
  CaseResource,
  MediaResource,
  CaseCollectionResource,
  ReplyResource,
  StudyResource,
  DiagnosisResource,
  TagResource,
  ThumbnailResource,
  AnnotationResource,
  QuizStudyResource,
  QuizCaseResource,
  RatingResource,
  QuizQuestionResource,
  CasePolicyResource,
  ApplicationEventLogResource,
  ApplicationEventResource,
  AiProviderResource,
  AiEngineResource,
  AnalysisResource,
  CaseGroupResource,
  PackageResource,
  ResearchRepositoryResource,
  ResearchProjectResource,
};
